import { commandSchema, newId, type Snapshot, type Command, type Venture } from './models';
export function dmChannel(a: string, b: string) {
  return `dm:${[a, b].sort().join(':')}`;
}
export function applyCommand(
  source: Snapshot,
  input: Command,
  actor: string,
  now = new Date().toISOString(),
): Snapshot {
  const c = commandSchema.parse(input);
  const s = JSON.parse(JSON.stringify(source)) as Snapshot;
  const user = s.profiles.find((p) => p.id === actor);
  if (!user) throw Error('Founder not found.');
  const bot = (id: string) => id === 'ai-assistant' || !!s.profiles.find((p) => p.id === id)?.is_ai;
  if (bot(actor) && !['message', 'connectionRespond', 'invitationRespond'].includes(c.type))
    throw Error('AI cannot initiate autonomous social actions.');
  const person = (id: string) => {
    if (!s.profiles.some((p) => p.id === id)) throw Error('Founder not found.');
  };
  const venture = (id: string) => {
    const v = s.ventures.find((v) => v.id === id);
    if (!v) throw Error('Venture not found.');
    return v;
  };
  const member = (id: string) => {
    const v = venture(id);
    if (!v.members.includes(actor)) throw Error('Join this venture before making changes.');
    return v;
  };
  const connected = (id: string) =>
    s.connections.some(
      (r) =>
        r.status === 'accepted' && [r.from, r.to].includes(actor) && [r.from, r.to].includes(id),
    );
  const members = (v: Venture, ids: string[]) => {
    if (ids.some((id) => !v.members.includes(id))) throw Error('Choose venture team members only.');
  };
  const channel = (id: string) => {
    if (id.startsWith('dm:')) {
      const pair = id.slice(3).split(':');
      if (pair.every(bot)) throw Error('Autonomous AI-to-AI messages are not allowed.');
      if (
        pair.length !== 2 ||
        !pair.includes(actor) ||
        !connected(pair.find((p) => p !== actor) ?? actor)
      )
        throw Error('Connect before starting a conversation.');
    } else member(id);
  };
  const activity = (
    title: string,
    kind: Snapshot['activities'][number]['kind'],
    ventureId?: string,
  ) =>
    s.activities.unshift({
      id: newId('activity'),
      title,
      detail: 'Recorded on platform',
      kind,
      ventureId,
      createdAt: now,
    });
  const unique = (items: { id: string }[], id: string) => {
    if (items.some((i) => i.id === id)) throw Error('This record already exists.');
  };
  switch (c.type) {
    case 'profile':
      if (
        c.profile.is_ai !== user.is_ai ||
        JSON.stringify(c.profile.ai) !== JSON.stringify(user.ai)
      )
        throw Error('Identity type cannot be changed.');
      if (c.profile.id !== actor) throw Error('You can only edit your own profile.');
      s.profiles = s.profiles.map((p) => (p.id === actor ? c.profile : p));
      break;
    case 'bookmark':
      person(c.personId);
      if (c.personId === actor) throw Error('Choose another founder.');
      s.bookmarks = s.bookmarks.filter((b) => !(b.userId === actor && b.personId === c.personId));
      if (c.state !== 'remove')
        s.bookmarks.push({
          id: `${actor}-${c.personId}`,
          userId: actor,
          personId: c.personId,
          state: c.state,
        });
      if (c.state === 'passed') {
        for (const r of s.connections) {
          if (
            r.status === 'pending' &&
            [r.from, r.to].includes(actor) &&
            [r.from, r.to].includes(c.personId)
          )
            r.status = 'declined';
        }
      }
      break;
    case 'connect': {
      person(c.personId);
      if (c.personId === actor) throw Error('Choose another founder.');
      const existing = s.connections.find(
        (r) =>
          r.status !== 'declined' &&
          [r.from, r.to].includes(actor) &&
          [r.from, r.to].includes(c.personId),
      );
      s.bookmarks = s.bookmarks.filter(
        (b) => !(b.userId === actor && b.personId === c.personId && b.state === 'passed'),
      );
      if (existing) {
        if (existing.status === 'pending' && existing.to === actor) {
          existing.status = 'accepted';
          activity(`${user.name.split(' ')[0]} made a mutual match`, 'connection');
        }
        // Retrying a like must never create another connection or imply mutual consent.
        break;
      }
      s.connections.push({
        id: newId('connection'),
        from: actor,
        to: c.personId,
        intro: c.intro,
        status: 'pending',
        createdAt: now,
      });
      activity(`${user.name.split(' ')[0]} liked a founder`, 'connection');
      break;
    }
    case 'connectionArchive': {
      const connection = s.connections.find((r) => r.id === c.id && [r.from, r.to].includes(actor));
      if (!connection) throw Error('Connection not found.');
      connection.status = 'declined';
      break;
    }
    case 'connectionRespond': {
      const r = s.connections.find((r) => r.id === c.id);
      if (!r || r.to !== actor || r.status !== 'pending')
        throw Error('This request is no longer available.');
      r.status = c.accept ? 'accepted' : 'declined';
      activity(c.accept ? 'A new connection begins' : 'Connection request declined', 'connection');
      break;
    }
    case 'message':
      channel(c.channel);
      if (c.replyTo && !s.messages.some((m) => m.id === c.replyTo && m.channel === c.channel))
        throw Error('Reply must belong to this conversation.');
      s.messages.push({
        id: newId('message'),
        channel: c.channel,
        author: actor,
        text: c.text,
        imageUrl: c.imageUrl,
        websiteCode: c.websiteCode,
        artifactId: c.artifactId,
        replyTo: c.replyTo,
        reference: c.reference,
        reactions: [],
        createdAt: now,
      });
      break;
    case 'react': {
      const m = s.messages.find((m) => m.id === c.id);
      if (!m) throw Error('Message not found.');
      channel(m.channel);
      m.reactions = m.reactions.includes(actor)
        ? m.reactions.filter((id) => id !== actor)
        : [...m.reactions, actor];
      break;
    }
    case 'venture': {
      unique(s.ventures, c.venture.id);
      const invitees = c.venture.members.filter((id) => id !== actor);
      if (invitees.some((id) => !connected(id))) throw Error('Invite accepted connections only.');
      s.ventures.push({ ...c.venture, members: [actor], createdAt: now });
      for (const to of invitees)
        s.invitations.push({
          id: newId('invite'),
          ventureId: c.venture.id,
          from: actor,
          to,
          kind: 'invitation',
          status: 'pending',
          createdAt: now,
        });
      activity(`${c.venture.name} is taking shape`, 'venture', c.venture.id);
      break;
    }
    case 'stage':
      member(c.ventureId).stage = c.stage;
      activity(`Venture moved to ${c.stage}`, 'venture', c.ventureId);
      break;
    case 'invite': {
      const v = member(c.ventureId);
      if (!connected(c.personId)) throw Error('Invite an accepted connection.');
      if (v.members.includes(c.personId)) throw Error('This person is already on the team.');
      if (
        s.invitations.some(
          (i) => i.ventureId === v.id && i.to === c.personId && i.status === 'pending',
        )
      )
        throw Error('An invitation is already pending.');
      s.invitations.push({
        id: newId('invite'),
        ventureId: v.id,
        from: actor,
        to: c.personId,
        kind: 'invitation',
        status: 'pending',
        createdAt: now,
      });
      activity('A teammate was invited', 'venture', v.id);
      break;
    }
    case 'join': {
      const v = venture(c.ventureId);
      if (v.members.includes(actor)) throw Error('You are already on this team.');
      if (v.stage === 'Closed') throw Error('This venture is closed.');
      if (
        s.invitations.some(
          (i) =>
            i.ventureId === v.id &&
            i.from === actor &&
            i.kind === 'request' &&
            i.status === 'pending',
        )
      )
        throw Error('Your request is already pending.');
      s.invitations.push({
        id: newId('join'),
        ventureId: v.id,
        from: actor,
        to: v.members[0]!,
        kind: 'request',
        status: 'pending',
        createdAt: now,
      });
      activity('A founder requested to join', 'venture', v.id);
      break;
    }
    case 'invitationRespond': {
      const i = s.invitations.find((i) => i.id === c.id);
      if (!i || i.status !== 'pending' || i.to !== actor)
        throw Error('This invitation is not available.');
      const v = venture(i.ventureId);
      if (i.kind === 'request') member(v.id);
      i.status = c.accept ? 'accepted' : 'declined';
      const joining = i.kind === 'request' ? i.from : i.to;
      if (c.accept && !v.members.includes(joining)) v.members.push(joining);
      activity(
        c.accept ? 'A teammate joined the venture' : 'Venture invitation declined',
        'venture',
        v.id,
      );
      break;
    }
    case 'decision': {
      const v = member(c.decision.ventureId);
      unique(s.decisions, c.decision.id);
      members(v, c.decision.participants);
      if (c.decision.deadline <= now) throw Error('Choose a future decision deadline.');
      s.decisions.push({
        ...c.decision,
        author: actor,
        votes: {},
        comments: [],
        closedAt: undefined,
        createdAt: now,
      });
      activity(c.decision.title, 'decision', v.id);
      break;
    }
    case 'vote': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      if (!d.participants.includes(actor))
        throw Error('You are not a participant in this decision.');
      if (d.closedAt || d.deadline <= now) throw Error('Voting has closed.');
      if (c.option >= d.options.length) throw Error('Choose a listed option.');
      d.votes[actor] = c.option;
      break;
    }
    case 'closeDecision': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      if (d.author !== actor) throw Error('Only the decision author can close it early.');
      if (d.closedAt) throw Error('This decision is already closed.');
      d.closedAt = now;
      activity(`Decision closed: ${d.title}`, 'decision', d.ventureId);
      break;
    }
    case 'decisionComment': {
      const d = s.decisions.find((d) => d.id === c.id);
      if (!d) throw Error('Decision not found.');
      member(d.ventureId);
      d.comments.push({ author: actor, text: c.text });
      break;
    }
    case 'commitment': {
      const v = member(c.commitment.ventureId);
      unique(s.commitments, c.commitment.id);
      members(v, [c.commitment.owner]);
      s.commitments.push({
        ...c.commitment,
        comments: [],
        completedAt: c.commitment.status === 'Completed' ? now : undefined,
        createdAt: now,
      });
      activity(`New commitment: ${c.commitment.title}`, 'commitment', v.id);
      break;
    }
    case 'commitmentStatus': {
      const t = s.commitments.find((t) => t.id === c.id);
      if (!t) throw Error('Commitment not found.');
      member(t.ventureId);
      t.status = c.status;
      t.completedAt = c.status === 'Completed' ? (t.completedAt ?? now) : undefined;
      activity(`${t.title} · ${c.status}`, 'commitment', t.ventureId);
      break;
    }
    case 'commitmentComment': {
      const t = s.commitments.find((t) => t.id === c.id);
      if (!t) throw Error('Commitment not found.');
      member(t.ventureId);
      t.comments.push({ author: actor, text: c.text });
      break;
    }
    case 'event': {
      const v = member(c.event.ventureId);
      unique(s.events, c.event.id);
      members(v, c.event.participants);
      s.events.push(c.event);
      activity(c.event.title, 'meeting', v.id);
      break;
    }
    case 'contribution': {
      const l = c.contribution;
      member(l.ventureId);
      unique(s.contributions, l.id);
      if (l.contributor !== actor) throw Error('Record your own contributions.');
      if (l.date > now.slice(0, 10))
        throw Error('Historical contributions cannot have a future date.');
      if (l.type !== 'Expense paid' && l.expenseMinor !== undefined)
        throw Error('Amounts belong only to historical expenses.');
      if (
        l.commitmentId &&
        !s.commitments.some((t) => t.id === l.commitmentId && t.ventureId === l.ventureId)
      )
        throw Error('Link a commitment from this venture.');
      s.contributions.push({ ...l, createdAt: now });
      activity(l.title, 'contribution', l.ventureId);
      break;
    }
    case 'endorse': {
      const e = c.endorsement;
      const v = member(e.ventureId);
      members(v, [e.to]);
      if (e.to === actor) throw Error('Endorse a teammate.');
      if (
        s.endorsements.some(
          (x) =>
            x.from === actor && x.to === e.to && x.skill === e.skill && x.ventureId === e.ventureId,
        )
      )
        throw Error('You have already recorded this endorsement.');
      s.endorsements.push({ ...e, from: actor });
      break;
    }
  }
  return s;
}
